import React, { useReducer, useContext, useEffect, useState } from 'react';
import useDataApi from '../hooks/useDataApi';
import useInterval from '../hooks/useInterval';
import constantes from '../resources/constantes.json';
import { toast } from 'react-toastify';
import { BodyNotificacion } from '../components/Notification';
import { parseaFechaHora } from '../utils';
import { useViewContext } from './viewContext';

/**
 * Contexto utilizado para contener el estado de las notificaciones
 */

const NotificationsContext = React.createContext();
const DispatchNotificationsContext = React.createContext();

const MAX_NOTIFICATIONS = 50;

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.add: {
      const unread = new Set(state.unread).add(action.notification.id);
      let notificationIds = [...state.notificationIds, action.notification.id];
      const notifications = {
        ...state.notifications,
        [action.notification.id]: action.notification,
      };
      const currentNotifications = state.currentNotifications.slice();
      currentNotifications[Number(action.notification.eleccion) - 1] =
        action.notification.id;

      const indexToRemove = notificationIds.findIndex(
        x => !currentNotifications.includes(x)
      );
      const idToRemove = notificationIds[indexToRemove];
      if (
        state.notificationIds.length >= MAX_NOTIFICATIONS &&
        idToRemove != null
      ) {
        notificationIds = notificationIds
          .slice(0, indexToRemove)
          .concat(notificationIds.slice(indexToRemove + 1));
        delete notifications[idToRemove];
        unread.delete(idToRemove);
        const read = new Set(state.read);
        read.delete(idToRemove);
        const hidden = new Set(state.hidden);
        hidden.delete(idToRemove);
        return {
          ...state,
          notifications,
          currentNotifications,
          notificationIds,
          unread,
          read,
          hidden,
        };
      }
      return {
        ...state,
        currentNotifications,
        notifications,
        notificationIds,
        unread,
      };
    }
    case actionTypes.addHidden: {
      const hidden = new Set(state.hidden).add(action.notification.id);
      let notificationIds = [...state.notificationIds, action.notification.id];
      const notifications = {
        ...state.notifications,
        [action.notification.id]: action.notification,
      };
      const currentNotifications = state.currentNotifications.slice();
      currentNotifications[Number(action.notification.eleccion) - 1] =
        action.notification.id;

      const indexToRemove = notificationIds.findIndex(
        x => !currentNotifications.includes(x)
      );
      const idToRemove = notificationIds[indexToRemove];
      if (
        state.notificationIds.length >= MAX_NOTIFICATIONS &&
        idToRemove != null
      ) {
        notificationIds = notificationIds
          .slice(0, indexToRemove)
          .concat(notificationIds.slice(indexToRemove + 1));
        delete notifications[idToRemove];
        hidden.delete(idToRemove);
        const unread = new Set(state.unread);
        unread.delete(idToRemove);
        const read = new Set(state.read);
        read.delete(idToRemove);
        return {
          ...state,
          notifications,
          currentNotifications,
          notificationIds,
          unread,
          read,
          hidden,
        };
      }
      return {
        ...state,
        notifications,
        currentNotifications,
        notificationIds,
        hidden: new Set(state.hidden).add(action.notification.id),
      };
    }
    case actionTypes.hideAll: {
      const hidden = new Set([...state.hidden, ...state.read, ...state.unread]);
      return {
        ...state,
        hidden,
        read: new Set(),
        unread: new Set(),
      };
    }
    case actionTypes.read: {
      if (!state.notifications.hasOwnProperty(action.id)) return state;
      const read = new Set(state.read).add(action.id);
      const unread = new Set(state.unread);
      unread.delete(action.id);
      return {
        ...state,
        read,
        unread,
      };
    }
    case actionTypes.readAll: {
      const read = new Set([...state.read, ...state.unread]);
      return {
        ...state,
        read,
        unread: new Set(),
      };
    }
    case actionTypes.unsuscribe: {
      return {
        ...state,
        fetchInterval: null,
      };
    }
    default:
      throw new Error('Invalid action type');
  }
};

export const actionTypes = {
  read: 'READ',
  readAll: 'READ_ALL',
  add: 'ADD',
  hideAll: 'HIDE_ALL',
  addHidden: 'ADD_HIDDEN',
  unsuscribe: 'UNSUSCRIBE',
};

const toastReducer = (state, action) => {
  switch (action.type) {
    case toastActionTypes.addToast: {
      return {
        ...state,
        toastQueue: [...state.toastQueue, action.id],
      };
    }
    case toastActionTypes.showToast: {
      return {
        ...state,
        showingToast: true,
      };
    }
    case toastActionTypes.removeToast: {
      return {
        ...state,
        toastQueue: state.toastQueue.slice(1),
        showingToast: false,
      };
    }
    case toastActionTypes.removeAll: {
      return {
        ...state,
        toastQueue: [],
        showingToast: false,
      };
    }
    default:
      throw new Error('Invalid action type');
  }
};

const toastActionTypes = {
  addToast: 'ADD_TOAST',
  removeToast: 'REMOVE_TOAST',
  showToast: 'SHOW_TOAST',
  removeAll: 'REMOVE_ALL_TOAST',
};

export function NotificationsProvider({ nomenclator, children }) {
  const [state, dispatch] = useReducer(reducer, {
    notifications: {},
    currentNotifications: [],
    notificationIds: [],
    unread: new Set(),
    read: new Set(),
    hidden: new Set(),
    fetchInterval: 30000,
  });
  const [toastState, toastDispatch] = useReducer(toastReducer, {
    toastQueue: [],
    showingToast: false,
  });

  const [version, setVersion] = useState(undefined);
  const [fetchState, setUrl] = useDataApi({
    initialUrl: constantes.json.notification,
    initialData: undefined,
    initialUrlInfo: undefined,
    log: false,
  });
  const { notifications, unread, hidden, fetchInterval, read } = state;
  const { toastQueue, showingToast } = toastState;

  const esHistorico = useViewContext().esHistorico;

  useEffect(() => {
    if (
      fetchState == null ||
      fetchState.data == null ||
      fetchState.data.dataDefinitions == null
    )
      return;

    //comprobar si ha cambiado el numero de version
    if (version == null) {
      setVersion(fetchState.data.version);
    } else {
      if (fetchState.data.version !== version) window.location.reload();
    }

    //no mostrar notificaciones si no esta la pestaña activa
    if (document.hidden) return;

    //no mostrar notificaciones para la primera lectura de datos
    const noNotifications = Object.entries(notifications).length === 0;
    for (const [k, v] of Object.entries(fetchState.data.dataDefinitions)) {
      const eleccion = constantes.abbrEleccionACodigoEleccion[k];
      const numact = v.version;
      const { mdhm } = v;
      const id = eleccion.toString() + numact.toString();
      if (hidden.has(id)) continue;
      const notification = {
        id,
        eleccion,
        numact,
        mdhm,
      };
      if (noNotifications) {
        dispatch({
          type: actionTypes.addHidden,
          notification,
        });
      } else if (!notifications.hasOwnProperty(id)) {
        dispatch({ type: actionTypes.add, notification });
        toastDispatch({ type: toastActionTypes.addToast, id });
      }
    }
  }, [fetchState, notifications, unread.size, hidden, nomenclator, version]);

  useEffect(() => {
    if (!showingToast) {
      toastQueue.forEach(element => {
        if (!unread.has(element)) {
          toastDispatch({ type: toastActionTypes.removeToast, element });
        } else {
          if (!esHistorico) {
            toast(
              <BodyNotificacion
                id={element}
                notification={notifications[element]}
                nomenclator={nomenclator}
              />,
              {
                onOpen: () => toastDispatch({ type: toastActionTypes.showToast }),
                onClose: ({ id }) =>
                  toastDispatch({ type: toastActionTypes.removeAll }),
              }
            );
          }

        }
      });
    }
  }, [toastQueue, nomenclator, notifications, showingToast, unread, esHistorico]);

  useInterval(() => {
    setUrl(constantes.json.notification, {}); //pasamos nuevo objeto para que siempre haga fetch;
  }, fetchInterval);

  return (
    <NotificationsContext.Provider value={state}>
      <DispatchNotificationsContext.Provider value={dispatch}>
        {children}
      </DispatchNotificationsContext.Provider>
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  return context;
}

export function useDispatchNotifications() {
  const context = useContext(DispatchNotificationsContext);
  return context;
}

export const getNotificationText = ({ eleccion, tope, mdhm, t }) => {
  return {
    title: `Nuevo ${tope === 1 ? 'boletín' : 'avance'} en ${t(
      constantes.nombreEleccion[eleccion]
    )}`,
    subtitle: `${parseaFechaHora(mdhm)}`,
  };
};
